<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createSftpConfig } from "./fs";
  import { makeFileSystemSftp, testSftpConnection } from "./fs-sftp";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createSftpConfig());
  let keyFileName = $state("");
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.host.trim() != "" &&
      config.user.trim() != "" &&
      (config.password != "" || config.key != "") &&
      !testingConnection,
  );

  function sftpWho(): string {
    let host = config.host.includes(":") ? `[${config.host}]` : config.host;
    if (config.port == "22") {
      return `${config.user}@${host}`;
    }
    return `${config.user}@${host}:${config.port}`;
  }

  async function onKeyFile(ev: Event) {
    let input = ev.target as HTMLInputElement;
    let file = input.files?.[0];
    errorMessage = "";
    if (!file) {
      config.key = "";
      keyFileName = "";
      return;
    }
    if (file.size > 64 * 1024) {
      config.key = "";
      keyFileName = "";
      errorMessage = "Error: private key is too long";
      return;
    }
    config.key = await file.text();
    keyFileName = file.name;
  }

  function clearKey(ev: Event) {
    ev.preventDefault();
    config.key = "";
    keyFileName = "";
    let input = document.getElementById("input-keyfile") as HTMLInputElement | null;
    if (input) {
      input.value = "";
    }
  }

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing sftp connection");
    try {
      config.host = config.host.trim();
      config.port = config.port.trim() || "22";
      config.user = config.user.trim();
      config.name = config.name.trim();
      let result = await testSftpConnection(config);
      config.hostKey = result.hostKey;
      if (config.name == "") {
        config.name = sftpWho();
      }
      let fs = makeFileSystemSftp(config);
      let who = config.name;
      telegramNotify(`New SFTP connection for ${who}`);
      captureEvent("sftp-login", { host: config.host });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect SFTP">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <div class="mt-3 flex gap-3">
        <div class="flex grow flex-col">
          <label for="input-host">Host:</label>
          <input
            type="text"
            id="input-host"
            autocomplete="off"
            class="outline-hidden border px-2 py-1"
            bind:value={config.host}
          />
        </div>
        <div class="flex w-20 flex-col">
          <label for="input-port">Port:</label>
          <input
            type="text"
            id="input-port"
            autocomplete="off"
            class="outline-hidden border px-2 py-1"
            bind:value={config.port}
          />
        </div>
      </div>

      <label class="mt-3" for="input-user">User:</label>
      <input
        type="text"
        id="input-user"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.user}
      />

      <label class="mt-3" for="input-password">Password:</label>
      <input
        type="password"
        id="input-password"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.password}
      />

      <label class="mt-3" for="input-keyfile">Private key:</label>
      <input type="file" id="input-keyfile" class="text-sm" onchange={onKeyFile} />
      {#if keyFileName}
        <div class="mt-1 flex items-center gap-2">
          <span class="max-w-64 truncate">{keyFileName}</span>
          <button type="button" class="text-gray-500 underline" onclick={clearKey}>clear</button>
        </div>
      {/if}

      <label class="mt-3" for="input-keypass">Key password:</label>
      <input
        type="password"
        id="input-keypass"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.keyPass}
      />

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect SFTP</button>
    </div>
  {/snippet}
</DialogBase>
